import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useProject, usePublishProject, useCancelProject } from '../../../api/projects';
import { useAuth } from '../../../hooks/useAuth';
import { ProjectStatus, UserRole } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { PageMeta } from '../shared/PageMeta';
import { AskCard } from './AskCard';
import { CreateAskForm } from './CreateAskForm';
import { MediaGallery } from './MediaGallery';
import { MediaUploader } from './MediaUploader';
import { OrgIcon, UserIcon, AskIcon } from '../shared/EntityIcons';

export function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const projectId = parseInt(id || '0', 10);
  const { data: project, isLoading, refetch } = useProject(projectId);
  const publishMutation = usePublishProject();
  const cancelMutation = useCancelProject();
  const [showCancel, setShowCancel] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  if (!project) {
    return <div className="container mx-auto px-4 py-16 text-center"><h2 className="text-2xl">Project Not Found</h2></div>;
  }

  const isOwner = user?.id === project.recipientId;
  const isAdmin = user?.role === UserRole.ADMIN;
  const canEdit = isOwner && (project.status === ProjectStatus.DRAFT || project.status === ProjectStatus.ACTIVE);
  const canPublish = isOwner && project.status === ProjectStatus.DRAFT;
  const canCancel = (isOwner || isAdmin) && project.status !== ProjectStatus.CANCELLED;

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title={project.title} description={project.description.slice(0, 160)} path={`/marketplace/projects/${project.id}`} />

      <Link to="/marketplace/projects" className="text-sm text-spi-green hover:underline mb-4 inline-block">&larr; Back to projects</Link>

      {/* Header */}
      <div className="card p-8 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl mb-1">{project.title}</h2>
            <p className="text-spi-slate">
              <Link to={`/marketplace/organizations/${project.organizationId}`} className="inline-flex items-center gap-1 hover:text-spi-green">
                <OrgIcon className="w-3.5 h-3.5" />{project.organizationName}
              </Link>
              {' '}&middot;{' '}
              <Link to={`/marketplace/users/${project.recipientId}`} className="inline-flex items-center gap-1 hover:text-spi-green">
                <UserIcon className="w-3.5 h-3.5" />{project.recipientName}
              </Link>
            </p>
          </div>
          <StatusBadge status={project.status} />
        </div>

        <p className="text-spi-charcoal mb-6 whitespace-pre-wrap">{project.description}</p>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div>
            <span className="text-xs font-semibold text-spi-slate">Country</span>
            <p className="font-medium">{project.deliveryCountry}</p>
          </div>
          {project.adaptationZone && (
            <div>
              <span className="text-xs font-semibold text-spi-slate">Adaptation Zone</span>
              <p className="font-medium">Zone {project.adaptationZone}</p>
            </div>
          )}
          <div>
            <span className="text-xs font-semibold text-spi-slate">Delivery Address</span>
            <p className="font-medium">{project.deliveryAddress}</p>
          </div>
          {project.neededBy && (
            <div>
              <span className="text-xs font-semibold text-spi-slate">Needed By</span>
              <p className="font-medium">{new Date(project.neededBy).toLocaleDateString()}</p>
            </div>
          )}
        </div>

        {project.links.length > 0 && (
          <div className="mb-6">
            <span className="text-xs font-semibold text-spi-slate">Links</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {project.links.map((link, i) => (
                <a key={i} href={link} target="_blank" rel="noreferrer" className="text-sm text-spi-green hover:underline">
                  {new URL(link).hostname}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {canPublish && (
            <button
              onClick={async () => {
                try {
                  await publishMutation.mutateAsync(project.id);
                  toast.success('Project published');
                } catch (err: unknown) {
                  toast.error((err as Error).message || 'Failed to publish');
                }
              }}
              disabled={publishMutation.isPending}
              className="btn btn-primary"
            >
              Publish Project
            </button>
          )}
          {canEdit && (
            <Link to={`/marketplace/projects/${project.id}/edit`} className="btn btn-outline">
              Edit Project
            </Link>
          )}
          {canCancel && (
            <button onClick={() => setShowCancel(true)} className="btn text-red-600 hover:bg-red-50 border border-red-200 rounded-xl px-6 py-3 font-semibold text-sm">
              Cancel Project
            </button>
          )}
        </div>
      </div>

      {/* Media */}
      <div className="mb-6">
        <MediaGallery media={project.media} canDelete={isOwner} />
        {isOwner && (
          <div className="mt-4">
            <MediaUploader projectId={project.id} />
          </div>
        )}
      </div>

      {/* Asks */}
      <div className="mb-6">
        <h3 className="text-xl mb-4 flex items-center gap-2"><AskIcon className="w-5 h-5 text-spi-green" />Seed Requests ({project.asks.length})</h3>
        {project.asks.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {project.asks.map((ask) => (
              <AskCard key={ask.id} ask={ask} projectRecipientId={project.recipientId} projectId={project.id} />
            ))}
          </div>
        ) : (
          <p className="text-spi-slate text-sm">No seed requests yet.</p>
        )}

        {canEdit && (
          <div className="mt-4">
            <CreateAskForm projectId={project.id} onCreated={() => refetch()} />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showCancel}
        title="Cancel Project"
        message="Are you sure you want to cancel this project? All open asks will also be cancelled."
        confirmLabel="Cancel Project"
        danger
        onConfirm={async () => {
          await cancelMutation.mutateAsync(project.id);
          toast.success('Project cancelled');
          setShowCancel(false);
          navigate('/marketplace/projects');
        }}
        onCancel={() => setShowCancel(false)}
      />
    </div>
  );
}
